import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  AccountTree,
  TrendingUp,
  PersonAdd,
  Assignment,
  Engineering,
  ReceiptLong,
  Payments,
  Analytics,
  Transform,
  AutoGraph,
  Timeline,
  Loop
} from '@mui/icons-material';

const PageContainer = styled.div`
  padding-top: 0;
  min-height: 100vh;
`;

const Hero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }

  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
    line-height: 1.6;
    position: relative;
    z-index: 2;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const FeatureSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  padding: 6rem 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even) {
    direction: rtl;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    direction: ltr;
    padding: 4rem 0;
  }
`;

const TextContent = styled(motion.div)`
  direction: ltr;
  
  h3 {
    font-size: 1rem;
    color: #4f46e5;
    margin-bottom: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  h2 {
    font-size: 2.25rem;
    color: #1f2937;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    line-height: 1.2;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 2rem;
    font-size: 1.125rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 2rem 0;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.25rem;
    color: #4b5563;
    font-size: 1.125rem;

    svg {
      color: #4f46e5;
      margin-top: 0.25rem;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  direction: ltr;
  
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }
`;

const features = [
  {
    icon: <PersonAdd sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Lead Capture & Management",
    subtitle: "Start the Journey",
    description: "Capture and nurture leads from multiple channels. Convert inquiries into opportunities with our intelligent lead management system.",
    benefits: [
      "Multi-channel lead capture",
      "Automated lead scoring",
      "Smart lead distribution",
      "Follow-up automation"
    ],
    image: "/images/features/lead-management.jpg"
  },
  {
    icon: <Assignment sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Estimate Creation & Conversion",
    subtitle: "Seamless Proposals",
    description: "Create professional estimates instantly and convert them into projects with a single click. Track acceptance rates and follow-ups automatically.",
    benefits: [
      "Digital estimate templates",
      "One-click project conversion",
      "Client e-signatures",
      "Estimate analytics"
    ],
    image: "/images/features/estimate-creation.jpg"
  },
  {
    icon: <Engineering sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Project Execution",
    subtitle: "Efficient Delivery",
    description: "Manage projects from start to finish with comprehensive tools for scheduling, resource allocation, and progress tracking.",
    benefits: [
      "Real-time project tracking",
      "Resource management",
      "Client communication portal",
      "Milestone tracking"
    ],
    image: "/images/features/project-execution.jpg"
  },
  {
    icon: <Loop sx={{ fontSize: 40, color: '#4f46e5' }} />,
    title: "Complete Business Cycle",
    subtitle: "End-to-End Solution",
    description: "Close the loop with integrated invoicing, payment processing, and automated follow-ups for future business opportunities.",
    benefits: [
      "Automated invoicing",
      "Integrated payments",
      "Client feedback collection",
      "Repeat business automation"
    ],
    image: "/images/features/business-cycle.jpg"
  }
];

const Ecosystem = () => {
  return (
    <PageContainer>
      <Hero>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Complete Business Ecosystem
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Transform leads into loyal customers with our integrated business ecosystem. From initial contact to final payment, manage your entire business workflow in one seamless platform.
        </motion.p>
      </Hero>
      
      <Content>
        {features.map((feature, index) => (
          <FeatureSection key={feature.title}>
            <TextContent
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>{feature.subtitle}</h3>
              <h2>{feature.icon} {feature.title}</h2>
              <p>{feature.description}</p>
              <ul>
                {feature.benefits.map((benefit, i) => (
                  <li key={i}>
                    <TrendingUp sx={{ fontSize: 20 }} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </TextContent>
            <ImageContainer
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img src={feature.image} alt={feature.title} />
            </ImageContainer>
          </FeatureSection>
        ))}
      </Content>
    </PageContainer>
  );
};

export default Ecosystem;